/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';
import {
  getUserLinkedInLink,
  getUserTwitterUsername,
  getUserFacebookLink,
  getUserGithubLink,
  getUserWebsite,
} from '@e180/domain/lib/user/util';

import styles from './UserSocial.style';

import withFeatureDecisions from 'src/shared/app/feature/component/withFeatureDecisions';
import useEventAccentColor from 'src/shared/domain/event/hook/useEventAccentColor';
import FacebookIcon from 'src/shared/ui/component/icon/FacebookIcon';
import LinkedInIcon from 'src/shared/ui/component/icon/LinkedInIcon';
import LinkIcon from 'src/shared/ui/component/icon/LinkIcon';
import TwitterIcon from 'src/shared/ui/component/icon/TwitterIcon';
import GithubIcon from 'src/shared/ui/component/icon/GithubIcon';

import type { User } from '@e180/domain/lib/user/type';

type Props = {
  user: User,
  // Self passed
  featureDecisions: Object,
};

const useStyles = createUseStyles(styles);

const UserSocial = ({ user, featureDecisions }: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const [accentColor] = useEventAccentColor();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  const linkedIn = getUserLinkedInLink(user);
  const twitter = getUserTwitterUsername(user);
  const facebook = getUserFacebookLink(user);
  const website = getUserWebsite(user);
  const github = getUserGithubLink(user);

  const socialProps = [];

  if (linkedIn) {
    socialProps.push({
      url: linkedIn,
      icon: <LinkedInIcon />,
    });
  }

  if (twitter) {
    socialProps.push({
      url: `https://twitter.com/${twitter.replace('@', '')}`,
      icon: <TwitterIcon />,
    });
  }

  if (facebook) {
    socialProps.push({
      url: facebook,
      icon: <FacebookIcon />,
    });
  }

  // Needs feature decisions to be coherent only. Does not affect end user.
  if (featureDecisions.isGithubProfileEnabled() && github) {
    socialProps.push({
      url: github,
      icon: <GithubIcon />,
    });
  }

  if (website) {
    socialProps.push({
      url: website,
      icon: <LinkIcon />,
    });
  }

  // Styles

  const itemStyle = { fill: accentColor };

  // Elements

  const items = socialProps.map((item, index) => {
    const { url, icon } = item;

    return (
      <li className={classes.item} key={index}>
        <a
          style={itemStyle}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {icon}
        </a>
      </li>
    );
  });

  return <ul className={classes.root}>{items}</ul>;
};

export default compose(React.memo, withFeatureDecisions)(UserSocial);
